/** @module csi/action/DeleteController */
define([
	"dojo/_base/declare",
	"csi/action",
	"csi/action/Controller",
	"csi/locale",
	"csi/reflection",
	"csi/state/rootStore",
	"csi/widget/MessageDialog",
	"csi/xml"
], function(declare, csiAction, Controller, locale, reflection, {featureTogglesStore}, MessageDialog, xml) {

	return declare(Controller, {
		// Boolean: if true, then node will be deleted whether or not there is an aggregate reference to it
		bIgnoreUsage: false,

		// Boolean: true if node being deleted is currently selected
		bSelected: null,

		// String: url of next selected node if node being deleted was selected
		urlNext: null,
		
		preProcess: function() {
			// summary: confirm deletion before continuing
			if (this.refNode) {
				// highlight node if not already selected before showing confirmation dialog
				var fd = this.widget.defView.m_formData;
				if (fd && fd.isSelection(this.getContext())) {
					this.bSelected = true;
				} else {
					this.widget.highlight(this.refNode, false, true);
				}
			}

			if (this.shouldConfirm()) {
				this.confirmAction({});
			} else {
				this.inherited(arguments);
			}
		},

		/**
		 * Return resource to use for "Yes" button in confirmation dialog
		 * @return {string}
		 */
		getConfirmButton: function() {
			var a = this.m_action;
			return a.confirmAction || (a.bRemove ? MessageDialog.BUTTON_REMOVE : MessageDialog.BUTTON_DELETE);
		},

		confirmAction: function(/*Object*/params) {
			// summary: prompt user for response
			let node = this.getNode();
			const localeType = locale.type(node.$Type);

			if (!params.title) {
				params.title = this.getString(csiAction.SUFFIX_PROMPT, [localeType]);
			}
			
			const name = locale.getNodeName(node, this.m_cache);
			
			if (featureTogglesStore.ENABLE_ARCHIVING) {
				// C8-61628: the node needs to be refetched, because the node in cache might not have the referenced by archive flag
				// if in the meantime node referencing this node was archived
				this.m_cache.removeNode(node.$URL);
				node = this.m_cache.getNode(node.$URL, true);
				if (xml.nodeHasFlag(node, xml.NODE_FLAG_REFERENCED_BY_ARCHIVE_NODES)) {
					// replace default delete message
					// concatenate delete message that is coming from inheriting controller
					// or that is coming from different action id
					if (!params.message && this.m_action.getId() != "Delete") {
						params.message = this.getDeleteActionMessage(name, localeType);
					}
					const refByArchiveDeleteMessage = this.getString("_RefByArchive", name !== locale.getEmptyName() ? [name] : [localeType]);
					params.message = (params.message ?  params.message + "\n" : "") + refByArchiveDeleteMessage;
				}
			}

			if (!params.message) {
				params.message = this.getDeleteActionMessage(name, localeType);
			}
			this.inherited(arguments);
		},
		
		getDeleteActionMessage: function(name, localeType) {
			return name !== locale.getEmptyName() ? this.getString(csiAction.SUFFIX_CONFIRM, [name]) : this.getString("_NoNodeName", [localeType]);
		},

		shouldConfirm: function() {
			// summary: only need to confirm if really a delete action, or if custom confirmAction is specified
			var a = this.m_action;
			return !a.bRemove || a.confirmAction;
		},

		onConfirm: function(/*String*/key) {
			// summary: continue with delete if confirmed
			if (key == this.getConfirmButton()) {
				if (this.bSelected) {
					var view = this.widget;
					var fd = view.defView.m_formData;

					// find another node to select if one is required
					if (fd.m_bRequireOne && this.refNode) {
						var refNode = view.getRefNodeNext(this.refNode, false, true) || view.getRefNodeNext(this.refNode, true, true);
						if (refNode) {
							this.urlNext = view.getRefNodeUrl(refNode);
						}
					}

					// deselect node if it is currently selected and highlight instead - new node will be selected later
					fd.changeSelection(this.getContext(), false);

					if (this.refNode) {
						view.highlight(this.refNode, false, true);
					}
				}

					Controller.prototype.preProcess.call(this);
			} else {
				this.postProcess();
			}
		},

		toWriter: function() {
			var call = this.inherited(arguments);
			if (call) return call;

			var a = this.m_action;

			var removeChild = function(ac) {
				ac.m_writer.changeParent(ac.m_urlParent, ac.m_idAttrParent, ac.getParentAttributeType(), [ac.m_urlNode], false);
			};

			if (a.bRemove) {
				removeChild(this);
			} else {
				// delete if relation is composite or no parent or forced - otherwise just remove from parent
				var rcr = reflection.getRelation(this.getParent(), this.m_idAttrParent);

				if (a.bForce || !rcr || rcr.isComposite) {
					// this is needed to prevent CheckUsageOnDelete exception when delete is forced
					if (a.bForce && rcr && rcr.isAggregate()) {
						removeChild(this);
					}

					this.m_writer.deleteNode(this.m_urlNode, this.bIgnoreUsage);
				} else {
					removeChild(this);
				}
			}

			return null;
		},

		postProcess: function(bStatus) {
			// summary: unhighlight node being operated on
			if (this.refNode) {
				this.widget.highlight(this.refNode, true, true);
			}

			// re-select node if problem with deletion
			if (!bStatus && this.bSelected) {
				this.widget.defView.m_formData.changeSelection(this.getContext(), true);
			}

			this.inherited(arguments);
		},

		postProcessSuccess: function() {
			// summary: select new node if one is required
			if (this.urlNext) {
				this.widget.defView.m_formData.changeSelection(this.widget.getContext(this.urlNext), true);
			}

			this.inherited(arguments);
		},

		/**
		 * Get URL of group node
		 */
		_getGroupURL: function() {
			var view = this.widget;
			var refNodeId = this.refNode ? view.getRefNodeId(this.refNode) : null;
			var qr = refNodeId && view.getQueryResult && view.getQueryResult(refNodeId);
			return qr && qr.groupValue;
		},

		/**
		 * If deleting a group whose value is a valid URL, then operate on that URL rather than the group's row URL (which is a fake client URL)
		 */
		_writeRequestParameters: function(call) {
			var url = this.m_urlNode;
			var groupURL = this._getGroupURL();
			if (xml.isValidURL(groupURL)) {
				this.m_urlNode = groupURL;
			}
			this.inherited(arguments);
			this.m_urlNode = url;
		}
	});
});